import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/db/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Mail, Lock, Phone, MessageSquare, Loader2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

export default function LoginPage() {
  const navigate = useNavigate();
  const { signIn, signUp, signInWithPhone, signInWithEmailOTP, verifyEmailOTP } = useAuth();
  
  // 账号密码登录
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [username, setUsername] = useState('');
  
  // 短信验证码登录
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [smsSessionId, setSmsSessionId] = useState('');
  const [smsCountdown, setSmsCountdown] = useState(0);
  
  // 邮箱验证码登录
  const [emailOTP, setEmailOTP] = useState('');
  const [emailOTPCode, setEmailOTPCode] = useState('');
  const [emailOTPSent, setEmailOTPSent] = useState(false);
  
  const [loading, setLoading] = useState(false);

  // 账号密码登录/注册
  const handleEmailLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('请填写完整信息');
      return;
    }

    setLoading(true);
    try {
      if (isSignUp) {
        if (!username) {
          toast.error('请输入用户名');
          setLoading(false);
          return;
        }
        await signUp(email, password, username);
        toast.success('注册成功！');
      } else {
        await signIn(email, password);
        toast.success('登录成功！');
      }
      
      const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/';
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath);
    } catch (error: any) {
      console.error('登录失败:', error);
      
      // 中文化错误提示
      let errorMessage = '操作失败，请重试';
      
      if (error.message) {
        const msg = error.message.toLowerCase();
        
        if (msg.includes('user already registered') || msg.includes('already registered')) {
          errorMessage = '该账号已被注册';
          toast.error(errorMessage, {
            action: {
              label: '去登录',
              onClick: () => setIsSignUp(false),
            },
          });
          setLoading(false);
          return;
        } else if (msg.includes('invalid login credentials') || msg.includes('invalid credentials')) {
          errorMessage = '账号或密码错误';
        } else if (msg.includes('email not confirmed')) {
          errorMessage = '邮箱未验证，请查收验证邮件';
        } else if (msg.includes('invalid email')) {
          errorMessage = '邮箱格式不正确';
        } else if (msg.includes('password')) {
          errorMessage = '密码格式不正确（至少6位）';
        } else if (msg.includes('network')) {
          errorMessage = '网络连接失败，请检查网络';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 发送短信验证码
  const handleSendSMS = async () => {
    if (!phone) {
      toast.error('请输入手机号');
      return;
    }

    const phoneRegex = /^1[3-9]\d{9}$/;
    if (!phoneRegex.test(phone)) {
      toast.error('手机号格式不正确');
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('send-sms-code', {
        body: { mobile: phone },
      });

      if (error) throw error;
      if (data.error) throw new Error(data.error);

      setSmsSessionId(data.sessionId);
      toast.success('验证码已发送');
      
      // 开始倒计时
      setSmsCountdown(60);
      const timer = setInterval(() => {
        setSmsCountdown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error: any) {
      console.error('发送验证码失败:', error);
      toast.error(error.message || '发送失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 短信验证码登录
  const handleSMSLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!phone || !smsCode || !smsSessionId) {
      toast.error('请填写完整信息');
      return;
    }

    setLoading(true);
    try {
      await signInWithPhone(phone, smsCode, smsSessionId);
      toast.success('登录成功！');
      
      const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/';
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath);
    } catch (error: any) {
      console.error('登录失败:', error);
      toast.error(error.message || '登录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 发送邮箱验证码
  const handleSendEmailOTP = async () => {
    if (!emailOTP) {
      toast.error('请输入邮箱');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailOTP)) {
      toast.error('邮箱格式不正确');
      return;
    }

    setLoading(true);
    try {
      await signInWithEmailOTP(emailOTP);
      setEmailOTPSent(true);
      toast.success('验证码已发送到您的邮箱');
    } catch (error: any) {
      console.error('发送验证码失败:', error);
      toast.error(error.message || '发送失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 邮箱验证码登录
  const handleEmailOTPLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!emailOTP || !emailOTPCode) {
      toast.error('请填写完整信息');
      return;
    }

    setLoading(true);
    try {
      await verifyEmailOTP(emailOTP, emailOTPCode);
      toast.success('登录成功！');
      
      const redirectPath = sessionStorage.getItem('redirectAfterLogin') || '/';
      sessionStorage.removeItem('redirectAfterLogin');
      navigate(redirectPath);
    } catch (error: any) {
      console.error('登录失败:', error);
      toast.error(error.message || '验证失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/5 via-background to-secondary/5 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mb-2">
            <span className="text-3xl font-bold text-primary">万</span>
          </div>
          <CardTitle className="text-2xl font-bold">万象聚聚官 2135C</CardTitle>
          <CardDescription>选择您喜欢的方式登录</CardDescription>
        </CardHeader>
        
        <CardContent>
          <Tabs defaultValue="password" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="password">密码</TabsTrigger>
              <TabsTrigger value="sms">短信</TabsTrigger>
              <TabsTrigger value="email">邮箱</TabsTrigger>
            </TabsList>

            {/* 账号密码登录 */}
            <TabsContent value="password" className="space-y-4 mt-4">
              <form onSubmit={handleEmailLogin} className="space-y-4">
                {isSignUp && (
                  <div className="space-y-2">
                    <Label htmlFor="username">用户名</Label>
                    <Input
                      id="username"
                      placeholder="请输入用户名"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label htmlFor="email">邮箱</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      placeholder="请输入邮箱"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">密码</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="请输入密码"
                      className="pl-10 pr-10"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {isSignUp ? '注册' : '登录'}
                </Button>

                <div className="text-center text-sm">
                  <button
                    type="button"
                    onClick={() => setIsSignUp(!isSignUp)}
                    className="text-primary hover:underline"
                  >
                    {isSignUp ? '已有账号？立即登录' : '没有账号？立即注册'}
                  </button>
                </div>
              </form>
            </TabsContent>

            {/* 短信验证码登录 */}
            <TabsContent value="sms" className="space-y-4 mt-4">
              <form onSubmit={handleSMSLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="phone">手机号</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="请输入手机号"
                      className="pl-10"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="smsCode">验证码</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        id="smsCode"
                        placeholder="请输入验证码"
                        className="pl-10"
                        value={smsCode}
                        onChange={(e) => setSmsCode(e.target.value)}
                        disabled={loading}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleSendSMS}
                      disabled={loading || smsCountdown > 0}
                      className="shrink-0"
                    >
                      {smsCountdown > 0 ? `${smsCountdown}秒` : '获取验证码'}
                    </Button>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={loading}>
                  {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  登录
                </Button>
              </form>
            </TabsContent>

            {/* 邮箱验证码登录 */}
            <TabsContent value="email" className="space-y-4 mt-4">
              <form onSubmit={handleEmailOTPLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="emailOTP">邮箱</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="emailOTP"
                      type="email"
                      placeholder="请输入邮箱"
                      className="pl-10"
                      value={emailOTP}
                      onChange={(e) => setEmailOTP(e.target.value)}
                      disabled={loading || emailOTPSent}
                    />
                  </div>
                </div>

                {!emailOTPSent ? (
                  <Button
                    type="button"
                    className="w-full"
                    onClick={handleSendEmailOTP}
                    disabled={loading}
                  >
                    {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    发送验证码
                  </Button>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label htmlFor="emailOTPCode">验证码</Label>
                      <div className="relative">
                        <MessageSquare className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="emailOTPCode"
                          placeholder="请输入邮箱验证码"
                          className="pl-10"
                          value={emailOTPCode}
                          onChange={(e) => setEmailOTPCode(e.target.value)}
                          disabled={loading}
                        />
                      </div>
                    </div>

                    <Button type="submit" className="w-full" disabled={loading}>
                      {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      登录
                    </Button>

                    <div className="text-center text-sm">
                      <button
                        type="button"
                        onClick={() => {
                          setEmailOTPSent(false);
                          setEmailOTPCode('');
                        }}
                        className="text-primary hover:underline"
                      >
                        重新发送
                      </button>
                    </div>
                  </>
                )}
              </form>
            </TabsContent>
          </Tabs>

          {/* 微信登录（暂未实现） */}
          <div className="mt-6 pt-6 border-t">
            <Button
              variant="outline"
              className="w-full justify-start"
              disabled
              onClick={() => {/* 微信登录功能即将开放 */}}
            >
              <svg className="mr-2 h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 0 1 .213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 0 0 .167-.054l1.903-1.114a.864.864 0 0 1 .717-.098 10.16 10.16 0 0 0 2.837.403c.276 0 .543-.027.811-.05-.857-2.578.157-4.972 1.932-6.446 1.703-1.415 3.882-1.98 5.853-1.838-.576-3.583-4.196-6.348-8.596-6.348zM5.785 5.991c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178A1.17 1.17 0 0 1 4.623 7.17c0-.651.52-1.18 1.162-1.18zm5.813 0c.642 0 1.162.529 1.162 1.18a1.17 1.17 0 0 1-1.162 1.178 1.17 1.17 0 0 1-1.162-1.178c0-.651.52-1.18 1.162-1.18zm5.34 2.867c-1.797-.052-3.746.512-5.28 1.786-1.72 1.428-2.687 3.72-1.78 6.22.942 2.453 3.666 4.229 6.884 4.229.826 0 1.622-.12 2.361-.336a.722.722 0 0 1 .598.082l1.584.926a.272.272 0 0 0 .14.047c.134 0 .24-.111.24-.247 0-.06-.023-.12-.038-.177l-.327-1.233a.582.582 0 0 1 .023-.156.49.49 0 0 1 .201-.398C23.024 18.48 24 16.82 24 14.98c0-3.21-2.931-5.837-6.656-6.088V8.89c-.135-.01-.27-.027-.407-.03zm-2.53 3.274c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982zm4.844 0c.535 0 .969.44.969.982a.976.976 0 0 1-.969.983.976.976 0 0 1-.969-.983c0-.542.434-.982.969-.982z"/>
              </svg>
              微信快捷登录（即将开放）
            </Button>
          </div>

          {/* 隐私协议 */}
          <div className="mt-6 text-center text-xs text-muted-foreground">
            登录即表示您同意我们的
            <button className="text-primary hover:underline mx-1">服务条款</button>
            和
            <button className="text-primary hover:underline mx-1">隐私政策</button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
